import { supabase } from './supabase';

// Types
export interface PlatformSetting {
  key: string;
  value: any;
  description: string | null;
  updated_at: string;
}

export interface LeadPricingConfig {
  id: string;
  contact_method: string;
  subscription_tier: string;
  price_cents: number;
  is_billable: boolean;
  display_name: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LeadPricingAudit {
  id: string;
  pricing_config_id: string | null;
  contact_method: string;
  subscription_tier: string;
  old_price_cents: number | null;
  new_price_cents: number | null;
  old_is_billable: boolean | null;
  new_is_billable: boolean | null;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface BusinessLeadSettings {
  business_id: string;
  monthly_budget_cents: number;
  budget_warning_threshold_percent: number;
  budget_action: 'notify' | 'pause' | 'pause_and_notify';
  grace_period_hours: number;
  phone_enabled: boolean;
  booking_enabled: boolean;
  message_enabled: boolean;
  website_enabled: boolean;
  dedup_window_days: number;
  notify_on_new_lead: boolean;
  notify_on_budget_warning: boolean;
  notify_on_budget_exceeded: boolean;
  created_at: string;
  updated_at: string;
}

export interface BusinessLead {
  id: string;
  business_id: string;
  user_id: string | null;
  consumer_phone_last4: string | null;
  highest_contact_method: string;
  highest_contact_at: string;
  contact_methods_used: string[];
  contact_count: number;
  first_contact_at: string;
  last_contact_at: string;
  billing_period_start: string;
  billing_period_end: string;
  billable_amount_cents: number;
  billing_status: string;
  billed_at: string | null;
  lead_outcome: string | null;
  created_at: string;
  updated_at: string;
}

export interface BusinessLeadBudgetUsage {
  id: string;
  business_id: string;
  period_start: string;
  period_end: string;
  leads_total: number;
  leads_included: number;
  leads_billable: number;
  leads_budget_exceeded: number;
  amount_included_cents: number;
  amount_billed_cents: number;
  amount_pending_cents: number;
  budget_limit_cents: number;
  budget_used_cents: number;
  budget_warning_sent_at: string | null;
  budget_exceeded_at: string | null;
  contact_methods_paused_at: string | null;
  grace_period_ends_at: string | null;
  created_at: string;
  updated_at: string;
}

// Platform Settings API
export async function getLeadBillingSettings(): Promise<PlatformSetting[]> {
  const { data, error } = await supabase
    .from('platform_settings')
    .select('*')
    .like('key', 'lead_billing_%')
    .order('key');

  if (error) throw error;
  return data || [];
}

export async function updatePlatformSetting(key: string, value: any): Promise<void> {
  const { error } = await supabase
    .from('platform_settings')
    .update({
      value,
      updated_at: new Date().toISOString()
    })
    .eq('key', key);

  if (error) throw error;
}

// Lead Pricing API
export async function getLeadPricingConfigs(): Promise<LeadPricingConfig[]> {
  const { data, error } = await supabase
    .from('lead_pricing_config')
    .select('*')
    .order('display_order');

  if (error) throw error;
  return data || [];
}

export async function updateLeadPricingConfig(
  id: string,
  updates: Partial<LeadPricingConfig>,
  reason?: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  const { error } = await supabase
    .from('lead_pricing_config')
    .update({
      ...updates,
      updated_by: user?.id,
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (error) throw error;
}

export async function createLeadPricingConfig(
  config: Omit<LeadPricingConfig, 'id' | 'created_at' | 'updated_at'>
): Promise<LeadPricingConfig> {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('lead_pricing_config')
    .insert({
      ...config,
      updated_by: user?.id
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Pricing Audit API
export async function getLeadPricingAudit(limit = 50): Promise<LeadPricingAudit[]> {
  const { data, error } = await supabase
    .from('lead_pricing_audit')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

// Business Lead Settings API
export async function getBusinessLeadSettings(businessId: string): Promise<BusinessLeadSettings | null> {
  const { data, error } = await supabase
    .from('business_lead_settings')
    .select('*')
    .eq('business_id', businessId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

export async function getAllBusinessLeadSettings(): Promise<BusinessLeadSettings[]> {
  const { data, error } = await supabase
    .from('business_lead_settings')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Business Leads API
export async function getBusinessLeads(
  businessId?: string,
  options?: {
    billingPeriodStart?: string;
    billingStatus?: string;
    limit?: number;
    offset?: number;
  }
): Promise<BusinessLead[]> {
  let query = supabase
    .from('business_leads')
    .select('*')
    .order('created_at', { ascending: false });

  if (businessId) {
    query = query.eq('business_id', businessId);
  }

  if (options?.billingPeriodStart) {
    query = query.eq('billing_period_start', options.billingPeriodStart);
  }

  if (options?.billingStatus) {
    query = query.eq('billing_status', options.billingStatus);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

export async function updateLeadStatus(
  leadId: string,
  status: string,
  notes?: string
): Promise<void> {
  const updates: any = { billing_status: status };

  if (status === 'billed') {
    updates.billed_at = new Date().toISOString();
  }

  if (notes) {
    updates.outcome_notes = notes;
  }

  const { error } = await supabase
    .from('business_leads')
    .update(updates)
    .eq('id', leadId);

  if (error) throw error;
}

// Budget Usage API
export async function getBusinessBudgetUsage(
  businessId?: string,
  periodStart?: string
): Promise<BusinessLeadBudgetUsage[]> {
  let query = supabase
    .from('business_lead_budget_usage')
    .select('*')
    .order('period_start', { ascending: false });

  if (businessId) {
    query = query.eq('business_id', businessId);
  }

  if (periodStart) {
    query = query.eq('period_start', periodStart);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

// Analytics
export interface LeadBillingStats {
  totalLeads: number;
  totalRevenue: number;
  averageLeadValue: number;
  leadsByMethod: Record<string, number>;
  leadsByStatus: Record<string, number>;
  topBusinesses: Array<{
    business_id: string;
    business_name: string;
    lead_count: number;
    total_spent: number;
  }>;
}

export async function getLeadBillingStats(
  periodStart?: string,
  periodEnd?: string
): Promise<LeadBillingStats> {
  const now = new Date();
  const defaultPeriodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const defaultPeriodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const start = periodStart || defaultPeriodStart;
  const end = periodEnd || defaultPeriodEnd;

  // Get leads for the period
  const { data: leads, error: leadsError } = await supabase
    .from('business_leads')
    .select('*')
    .gte('billing_period_start', start)
    .lte('billing_period_start', end);

  if (leadsError) throw leadsError;

  const leadData = leads || [];

  // Calculate stats
  const totalLeads = leadData.length;
  const totalRevenue = leadData.reduce((sum, l) => sum + (l.billable_amount_cents || 0), 0);
  const averageLeadValue = totalLeads > 0 ? totalRevenue / totalLeads : 0;

  // Group by contact method
  const leadsByMethod: Record<string, number> = {};
  leadData.forEach(l => {
    const method = l.highest_contact_method;
    leadsByMethod[method] = (leadsByMethod[method] || 0) + 1;
  });

  // Group by status
  const leadsByStatus: Record<string, number> = {};
  leadData.forEach(l => {
    const status = l.billing_status;
    leadsByStatus[status] = (leadsByStatus[status] || 0) + 1;
  });

  // Top businesses (aggregate manually)
  const businessStats: Record<string, { lead_count: number; total_spent: number }> = {};
  leadData.forEach(l => {
    if (!businessStats[l.business_id]) {
      businessStats[l.business_id] = { lead_count: 0, total_spent: 0 };
    }
    businessStats[l.business_id].lead_count += 1;
    businessStats[l.business_id].total_spent += l.billable_amount_cents || 0;
  });

  // Get business names
  const businessIds = Object.keys(businessStats);
  const { data: businesses } = await supabase
    .from('business_profiles')
    .select('business_id, company_name')
    .in('business_id', businessIds);

  const businessNameMap: Record<string, string> = {};
  (businesses || []).forEach(b => {
    businessNameMap[b.business_id] = b.company_name || 'Unknown';
  });

  const topBusinesses = Object.entries(businessStats)
    .map(([business_id, stats]) => ({
      business_id,
      business_name: businessNameMap[business_id] || 'Unknown',
      lead_count: stats.lead_count,
      total_spent: stats.total_spent
    }))
    .sort((a, b) => b.total_spent - a.total_spent)
    .slice(0, 10);

  return {
    totalLeads,
    totalRevenue,
    averageLeadValue,
    leadsByMethod,
    leadsByStatus,
    topBusinesses
  };
}
